import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NormalSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import { AiChatService } from '@qtek/libs/store';
import { Observable, Subject, debounceTime } from 'rxjs';

/**
 * One bot turn can fold in several preferences — the memory extractor saves each extracted fact
 * separately, so a single exchange emits one `chatPref` frame per fact. Coalesce them into one
 * notice rather than stacking a toast per fact.
 */
const NOTICE_DEBOUNCE_MS = 1000;

/** How long the toast stays up before auto-dismissing. */
const NOTICE_DURATION_MS = 6000;

/**
 * SD-2096: tells the user their Allo memory changed while the settings panel was closed.
 *
 * <p>Preferences change from inside the chat thread — the user asks Allo to remember or drop
 * something and the BE folds it in after the turn — so a change can land with nothing on screen
 * to show it. An open panel already re-reads itself ({@link AiChatSettingsComponent}), so this
 * service deliberately stays quiet then: a notice about something the user is already looking at
 * is noise.
 *
 * <p>Two signals, because a toast alone is missable and a badge alone is easy to overlook:
 * {@link unseenChange} drives a dot on the settings button until the panel is next opened, and a
 * snack bar offers to open it straight away.
 *
 * <p>Root-provided, so the popped-out chat window shares one instance with the sidenav — they run
 * in the same Angular application (the popout is a `DomPortalOutlet` into a second browser
 * window), and a change concerns the user, not a particular window.
 */
@Injectable({ providedIn: 'root' })
export class AlloMemoryNoticeService {
  private readonly chatSvc = inject(AiChatService);
  private readonly snackBar = inject(SnackBarService);

  private readonly reviewRequested = new Subject<void>();

  /** Mounted settings panels. The panel registers itself, since it is what "open" means here. */
  private openPanels = 0;

  /**
   * Popped-out chat windows. Tracked as the `Window` objects themselves rather than a mounted /
   * unmounted count: closing that browser window does not reliably destroy the component inside
   * it, so a lifecycle count would stick at "open" forever and silence the toast for good.
   * {@link hasOpenPopout} reads `Window.closed`, which cannot go stale.
   */
  private readonly popoutWindows = new Set<Window>();

  /**
   * Preferences changed while no panel was open, and the user has not looked since. Hosts render
   * this as a dot on the settings button.
   */
  readonly unseenChange = signal(false);

  /**
   * The user asked to see what changed. Hosts open their own settings panel — which host is on
   * screen is theirs to know, not this service's.
   */
  readonly reviewRequested$: Observable<void> =
    this.reviewRequested.asObservable();

  constructor() {
    this.chatSvc.chatPrefChanged$
      .pipe(debounceTime(NOTICE_DEBOUNCE_MS), takeUntilDestroyed())
      .subscribe(() => this.notify());
  }

  /** The settings panel mounted. Seeing it is what clears the badge. */
  panelOpened(): void {
    this.openPanels++;
    this.unseenChange.set(false);
  }

  /** The settings panel unmounted. */
  panelClosed(): void {
    this.openPanels = Math.max(0, this.openPanels - 1);
  }

  /**
   * The chat popped out into {@code popoutWindow}. Registering it keeps the toast from being
   * raised into a window the user has left — see {@link notify}.
   */
  registerPopoutWindow(popoutWindow: Window): void {
    this.popoutWindows.add(popoutWindow);
  }

  private notify(): void {
    // The open panel re-read itself already — the user is looking at the new state.
    if (this.openPanels > 0) return;

    this.unseenChange.set(true);

    // The snack bar renders into the main document's overlay, but a user with the chat popped out
    // is looking at the other window — a toast there would go unseen while stealing attention from
    // the page they left behind. The popped-out header carries the same badge, so drop the toast
    // and let that do the telling.
    if (this.hasOpenPopout()) return;

    this.snackBar.showSnackBar({
      type: 'NormalMessage',
      message: 'GNR_ALLO_MEMORY_UPDATED',
      icon: 'psychology',
      duration: NOTICE_DURATION_MS,
      actions: [
        {
          label: 'GNR_ALLO_MEMORY_REVIEW',
          callback: () => this.reviewRequested.next(),
        },
      ],
    } as NormalSnackBarData);
  }

  /** True while at least one popped-out chat window is still open. Forgets closed ones as it goes. */
  private hasOpenPopout(): boolean {
    for (const popoutWindow of this.popoutWindows) {
      if (popoutWindow.closed) {
        this.popoutWindows.delete(popoutWindow);
      }
    }
    return this.popoutWindows.size > 0;
  }
}
